import { generatedRedirectMap } from './generatedRedirectMap';
import { normalizeLegacyUrl, normalizeLegacyUrlParts } from './normalizeLegacyUrl';
import type { LegacyRedirectEntry } from './types';

export interface LegacyRedirectMatch {
  targetPath: string;
  reason: string;
}

interface LegacyRedirectCandidate extends LegacyRedirectMatch {
  normalizedTargetKey: string;
}

function normalizeEntry(entry: LegacyRedirectEntry) {
  const normalizedSource = normalizeLegacyUrlParts({
    host: entry.sourceHost,
    path: entry.sourcePath,
    query: entry.sourceQuery,
  });

  return {
    ...entry,
    ...normalizedSource,
  };
}

function normalizeTargetUrl(targetPath: string, currentUrl: URL) {
  return normalizeLegacyUrl(new URL(targetPath, currentUrl));
}

function resolveDeterministicMatch(candidates: LegacyRedirectCandidate[]) {
  const uniqueCandidates = new Map<string, LegacyRedirectMatch>();

  for (const candidate of candidates) {
    const nextCandidate = {
      targetPath: candidate.targetPath,
      reason: candidate.reason,
    };
    const existingCandidate = uniqueCandidates.get(candidate.normalizedTargetKey);

    if (!existingCandidate) {
      uniqueCandidates.set(candidate.normalizedTargetKey, nextCandidate);
      continue;
    }

    if (nextCandidate.targetPath < existingCandidate.targetPath) {
      uniqueCandidates.set(candidate.normalizedTargetKey, nextCandidate);
      continue;
    }

    if (nextCandidate.targetPath === existingCandidate.targetPath && nextCandidate.reason < existingCandidate.reason) {
      uniqueCandidates.set(candidate.normalizedTargetKey, nextCandidate);
    }
  }

  if (uniqueCandidates.size !== 1) {
    return null;
  }

  return uniqueCandidates.values().next().value ?? null;
}

export function matchLegacyRedirect(
  currentUrlLike: string | URL,
  redirectEntries: LegacyRedirectEntry[] = generatedRedirectMap,
): LegacyRedirectMatch | null {
  const currentUrl = currentUrlLike instanceof URL ? currentUrlLike : new URL(currentUrlLike);
  const normalizedCurrentUrl = normalizeLegacyUrl(currentUrl);
  const normalizedCurrentUrlWithoutQuery = normalizeLegacyUrlParts({
    host: normalizedCurrentUrl.host,
    path: normalizedCurrentUrl.path,
  });
  const queryAwareMatches: LegacyRedirectCandidate[] = [];
  const querylessMatches: LegacyRedirectCandidate[] = [];

  for (const redirectEntry of redirectEntries) {
    const normalizedEntry = normalizeEntry(redirectEntry);
    const entryDependsOnQuery = normalizedEntry.query.length > 0;
    const matches = entryDependsOnQuery
      ? normalizedCurrentUrl.key === normalizedEntry.key
      : normalizedCurrentUrlWithoutQuery.key === normalizedEntry.key;

    if (!matches) {
      continue;
    }

    const normalizedTargetUrl = normalizeTargetUrl(redirectEntry.targetPath, currentUrl);

    if (normalizedTargetUrl.key === normalizedCurrentUrl.key) {
      continue;
    }

    const match = {
      targetPath: redirectEntry.targetPath,
      reason: redirectEntry.reason,
      normalizedTargetKey: normalizedTargetUrl.key,
    };

    if (entryDependsOnQuery) {
      queryAwareMatches.push(match);
      continue;
    }

    querylessMatches.push(match);
  }

  const resolvedQueryAwareMatch = resolveDeterministicMatch(queryAwareMatches);

  if (resolvedQueryAwareMatch) {
    return resolvedQueryAwareMatch;
  }

  if (queryAwareMatches.length > 0) {
    return null;
  }

  return resolveDeterministicMatch(querylessMatches);
}
